import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import {
  Search,
  Home,
  Briefcase,
  Mail,
  Github,
  Linkedin,
  Twitter,
  Download,
  X,
  Command,
  ArrowRight,
} from 'lucide-react';
import { useLanguage } from '../../context/LanguageContext';
import { getResumePath } from '../../data/resume';

export default function CommandPalette() {
  const { language, t } = useLanguage();
  const cp = t.commandPalette || {};

  const COMMANDS = useMemo(() => {
    const commands = cp.commands || {};
    return [
      {
        id: 'home',
        label: commands.home || 'Go to Home',
        icon: Home,
        action: 'navigate',
        path: '/',
        keywords: ['home', 'main', 'start', '主页', '首页'],
      },
    {
      id: 'experience',
      label: commands.experience || 'View Experience',
      icon: Briefcase,
      action: 'navigate',
      path: '/experience',
      keywords: ['experience', 'work', 'jobs', 'career', '经历', '履历', '工作'],
    },
    {
      id: 'contact',
      label: commands.contact || 'Contact Me',
      icon: Mail,
      action: 'navigate',
      path: '/contact-me',
      keywords: ['contact', 'email', 'reach', 'message', '联系', '邮箱'],
    },
    {
      id: 'resume',
      label: commands.resume || 'Download Resume',
      icon: Download,
      action: 'link',
      path: getResumePath(language),
      keywords: ['resume', 'cv', 'download', 'pdf', '简历', '下载'],
    },
    {
      id: 'github',
      label: commands.github || 'Open GitHub',
      icon: Github,
      action: 'external',
      path: 'https://github.com/nandanosql/',
      keywords: ['github', 'code', 'repos', '开源', '代码'],
    },
    {
      id: 'linkedin',
      label: commands.linkedin || 'Open LinkedIn',
      icon: Linkedin,
      action: 'external',
      path: 'https://www.linkedin.com/in/nandan-priyadarshi/',
      keywords: ['linkedin', 'professional', 'network', '领英'],
    },
    {
      id: 'twitter',
      label: commands.twitter || 'Open X (Twitter)',
      icon: Twitter,
      action: 'external',
      path: 'https://x.com/nandanpri',
      keywords: ['twitter', 'x', 'social', '推特'],
    },
  ];
}, [cp.commands, language]);

  const [isOpen, setIsOpen] = useState(false);
  const [search, setSearch] = useState('');
  const [selectedIndex, setSelectedIndex] = useState(0);
  const navigate = useNavigate();

  const filteredCommands = useMemo(() => {
    if (!search.trim()) return COMMANDS;
    
    const query = search.toLowerCase();
    return COMMANDS.filter(
      (cmd) =>
        cmd.label.toLowerCase().includes(query) ||
        cmd.keywords.some((k) => k.includes(query))
    );
  }, [search, COMMANDS]);

  useEffect(() => {
    setSelectedIndex(0);
  }, [search]);

  const executeCommand = useCallback((command) => {
    setIsOpen(false);
    setSearch('');

    switch (command.action) {
      case 'navigate':
        navigate(command.path);
        break;
      case 'link':
        window.open(command.path, '_blank');
        break;
      case 'external':
        window.open(command.path, '_blank', 'noopener,noreferrer');
        break;
      default:
        break;
    }
  }, [navigate]);

  useEffect(() => {
    const handleKeyDown = (e) => {
      // Open with Cmd+K or Ctrl+K
      if ((e.metaKey || e.ctrlKey) && e.key === 'k') {
        e.preventDefault();
        setIsOpen((prev) => !prev);
        return;
      }

      if (!isOpen) return;

      // Close with Escape
      if (e.key === 'Escape') {
        setIsOpen(false);
        setSearch('');
        return;
      }

      // Navigate with arrows
      if (e.key === 'ArrowDown') {
        e.preventDefault();
        setSelectedIndex((prev) =>
          prev < filteredCommands.length - 1 ? prev + 1 : 0
        );
        return;
      }

      if (e.key === 'ArrowUp') {
        e.preventDefault();
        setSelectedIndex((prev) =>
          prev > 0 ? prev - 1 : filteredCommands.length - 1
        );
        return;
      }

      // Execute with Enter
      if (e.key === 'Enter' && filteredCommands[selectedIndex]) {
        e.preventDefault();
        executeCommand(filteredCommands[selectedIndex]);
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [isOpen, filteredCommands, selectedIndex, executeCommand]);

  return (
    <>
      {/* Trigger hint */}
      <button
        onClick={() => setIsOpen(true)}
        className="fixed bottom-6 right-6 z-40 hidden lg:flex items-center gap-2 px-3 py-2 rounded-xl bg-white/5 border border-white/10 text-white/50 text-xs font-medium hover:bg-white/10 hover:text-white/70 transition-[background-color,border-color,color] duration-150 backdrop-blur-xl"
        aria-label="Open command palette"
      >
        <Command className="h-3 w-3" />
        <span>K</span>
      </button>

      <AnimatePresence>
        {isOpen && (
          <>
            {/* Backdrop */}
            <motion.div
              className="fixed inset-0 z-50 bg-black/60 backdrop-blur-sm"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              onClick={() => {
                setIsOpen(false);
                setSearch('');
              }}
            />

            {/* Palette */}
            <motion.div
              className="fixed left-1/2 top-[20%] z-50 w-full max-w-lg -translate-x-1/2"
              initial={{ opacity: 0, scale: 0.95, y: -20 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.95, y: -20 }}
              transition={{ type: 'spring', stiffness: 400, damping: 30 }}
            >
              <div className="mx-4 overflow-hidden rounded-2xl border border-white/10 bg-[#0B0B0F]/95 shadow-2xl shadow-black/50 backdrop-blur-xl">
                {/* Search input */}
                <div className="flex items-center gap-3 border-b border-white/10 px-4 py-4">
                  <Search className="h-5 w-5 text-white/40" />
                  <input
                    type="text"
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    placeholder={cp.placeholder || "Type a command or search..."}
                    className="flex-1 bg-transparent text-white placeholder:text-white/40 focus:outline-none"
                    autoFocus
                  />
                  <button
                    onClick={() => {
                      setIsOpen(false);
                      setSearch('');
                    }}
                    className="rounded-lg p-1 text-white/40 hover:bg-white/10 hover:text-white/70 transition-colors"
                  >
                    <X className="h-4 w-4" />
                  </button>
                </div>

                {/* Commands list */}
                <div className="max-h-[300px] overflow-y-auto p-2">
                  {filteredCommands.length === 0 ? (
                    <div className="py-8 text-center text-white/40 text-sm">
                      {cp.noResults || 'No commands found'}
                    </div>
                  ) : (
                    <div className="space-y-1">
                      {filteredCommands.map((command, index) => (
                        <button
                          key={command.id}
                          onClick={() => executeCommand(command)}
                          onMouseEnter={() => setSelectedIndex(index)}
                          className={`flex w-full items-center gap-3 rounded-xl px-3 py-3 text-left transition-colors ${
                            index === selectedIndex
                              ? 'bg-white/10 text-white'
                              : 'text-white/60 hover:bg-white/5 hover:text-white/80'
                          }`}
                        >
                          <div
                            className={`flex h-9 w-9 items-center justify-center rounded-lg ${
                              index === selectedIndex
                                ? 'bg-white/15'
                                : 'bg-white/5'
                            }`}
                          >
                            <command.icon className="h-4 w-4" />
                          </div>
                          <span className="flex-1 font-medium">
                            {command.label}
                          </span>
                          {index === selectedIndex && (
                            <ArrowRight className="h-4 w-4 text-white/40" />
                          )}
                        </button>
                      ))}
                    </div>
                  )}
                </div>

                {/* Footer hint */}
                <div className="border-t border-white/10 px-4 py-3">
                  <div className="flex items-center justify-between text-xs text-white/40">
                    <div className="flex items-center gap-4">
                      <span className="flex items-center gap-1">
                        <kbd className="rounded bg-white/10 px-1.5 py-0.5">↑</kbd>
                        <kbd className="rounded bg-white/10 px-1.5 py-0.5">↓</kbd>
                        <span className="ml-1">{cp.navigate || 'Navigate'}</span>
                      </span>
                      <span className="flex items-center gap-1">
                        <kbd className="rounded bg-white/10 px-1.5 py-0.5">↵</kbd>
                        <span className="ml-1">{cp.select || 'Select'}</span>
                      </span>
                    </div>
                    <span className="flex items-center gap-1">
                      <kbd className="rounded bg-white/10 px-1.5 py-0.5">esc</kbd>
                      <span className="ml-1">{cp.close || 'Close'}</span>
                    </span>
                  </div>
                </div>
              </div>
            </motion.div>
          </>
        )}
      </AnimatePresence>
    </>
  );
}
